import { type SceneSelections } from "~/features/product-configurator/configurator-types";
import { type SharedMaterialName } from "~/features/product-configurator/utils/scene-materials";
export const SCENE_MODEL_PATHS = {
  // sensors
  "os-dome": "/models/sensors/os-dome.glb",
  os0: "/models/sensors/os0.glb",
  "os0-rgb": "/models/sensors/os0-rgb.glb",
  os1: "/models/sensors/os1.glb",
  "os1-max": "/models/sensors/os1-max.glb",
  "os1-max-rgb": "/models/sensors/os1-max-rgb.glb",
  // caps
  "os0-cap": "/models/caps/os0-cap.glb",
  "os0-rgb-cap": "/models/caps/os0-rgb-cap.glb",
  "os1-cap": "/models/caps/os1-cap.glb",
  "os1-max-cap": "/models/caps/os1-max-cap.glb",
  "os1-max-rgb-cap": "/models/caps/os1-max-rgb-cap.glb",
  // baseplate and cables
  cable: "/models/cables/default-cable.gltf",
  cableA: "/models/cables/cable-a.glb",
  cableB: "/models/cables/cable-b.glb",
  cableC: "/models/cables/cable-c.glb",
  interfaceBox: "/models/interface-box.glb",
  baseplateUniversal: "/models/baseplate_osx_universal.glb"
} as const;
export type SceneModelKey = keyof typeof SCENE_MODEL_PATHS;
export const SENSOR_MODEL_BY_SELECTION = {
  "os-dome": "os-dome",
  os0: "os0",
  "os0-rgb": "os0-rgb",
  os1: "os1",
  "os1-max": "os1-max",
  "os1-max-rgb": "os1-max-rgb"
} as const;
export const DEFAULT_CAP_BY_SENSOR: Partial<Record<keyof typeof SENSOR_MODEL_BY_SELECTION, string>> = {
  os0: "os0-cap",
  "os0-rgb": "os0-rgb-cap",
  os1: "os1-cap",
  "os1-max": "os1-max-cap",
  "os1-max-rgb": "os1-max-rgb-cap"
};
const CAP_MODEL_BY_SELECTION: Record<string, SceneModelKey> = {
  "os0-cap": "os0-cap",
  "os0-rgb-cap": "os0-rgb-cap",
  "os1-cap": "os1-cap",
  "os1-max-cap": "os1-max-cap",
  "os1-max-rgb-cap": "os1-max-rgb-cap"
};
const BASEPLATE_MODEL_BY_SELECTION: Record<string, SceneModelKey> = {
  universal: "baseplateUniversal",
  standard: "baseplateUniversal",
  "standard-os2": "baseplateUniversal"
};
const CABLE_MODEL_BY_SELECTION: Record<string, SceneModelKey> = {
  pigtail: "cable",
  "y-junction": "cable",
  "cable-a": "cableA",
  "cable-b": "cableB",
  "cable-c": "cableC",
  "interface-box": "interfaceBox"
};
export const EXTRUDED_CABLE_MODELS = new Set<SceneModelKey>(["cable"]);

// Per-model overrides for material names found inside the GLB. Use when an asset reuses
// a generic material name (e.g. "Glass") that should resolve to a different shared material
// for that one model.
export const MODEL_MATERIAL_OVERRIDES: Partial<Record<SceneModelKey, Record<string, SharedMaterialName>>> = {
  interfaceBox: {
    InterfaceBox_Glass: "Glass_Transparent"
  }
};
export type ResolvedModels = {
  sensor: SceneModelKey;
  cap?: SceneModelKey;
  baseplate?: SceneModelKey;
  cable?: SceneModelKey;
};
function resolveModel(selection: string | undefined, map: Record<string, SceneModelKey>): SceneModelKey | undefined {
  return selection ? map[selection] : undefined;
}
export function resolveSceneModels(selections: SceneSelections): ResolvedModels {
  return {
    sensor: resolveModel(selections.sensor, SENSOR_MODEL_BY_SELECTION) ?? "os-dome",
    cap: resolveModel(selections.cap, CAP_MODEL_BY_SELECTION),
    baseplate: resolveModel(selections.baseplate, BASEPLATE_MODEL_BY_SELECTION),
    cable: resolveModel(selections.cable, CABLE_MODEL_BY_SELECTION)
  };
}